/* eslint-disable @typescript-eslint/no-floating-promises */
import { useFeature } from '@optimizely/react-sdk';
import { isEmpty, xor } from 'lodash';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useMemo, useState, useEffect } from 'react';
import * as SDK from '@replai-platform/sdk';
import {
  Button,
  Colors,
  DropDownButton,
  DropDownChip,
  MultiSelectDropDownOption,
  Tabs,
  Toggle,
  Typography,
  camelCaseToCapitalCase,
} from '@replai-platform/ui-components';
import { useNavigate } from 'react-router';
import TopNavPageTitle from '../../../components/TopNavPageTitle';
import useCountryFilter from '../../../components/FilterBar/hooks/useCountryFilter';
import type { KpiFilterProps } from '../../../components/FilterBar/Filters/KpiFilter';
import CountryFilter, { CountryFilterProps } from '../../../components/FilterBar/Filters/CountryFilter';
import { RootState } from '../../../store/rootReducer';
import {
  DateFilter,
  type CampaignFilterProps,
  type CampaignTypeFilterProps,
  AdSetFilter,
  AdSetFilterProps,
  BuyingStrategyFilterProps,
  CampaignFilter,
  KpiFilter,
  SpendFilter,
  PromotedObjectFilter,
  PromotedObjectFilterProps,
  CustomTagsFilter,
  CustomTagsFilterProps,
  AgeDateFilter,
  NetworkFilterProps,
  NetworkFilter,
  BuyingStrategyFilter,
  CampaignTypeFilter,
  MarketAppsFilter,
  MarketAppsFilterProps,
  CustomMarketTagsFilterProps,
  CustomMarketTagsFilter,
} from '../../../components/FilterBar/Filters';
import useAdSetFilter from '../../../components/FilterBar/hooks/useAdSetFilter';
import useCampaignFilter from '../../../components/FilterBar/hooks/useCampaignFilter';
import useBuyingStrategyFilter from '../../../components/FilterBar/hooks/useBuyingStrategyFilter';
import { GeneratingReportNotificationStatus, ReportsActions } from '../../../store/reports';
import { TypographyClean } from '../../../utils/styles';
import { api } from '../../../api';
import useGenerateGoogleSlidesReport from '../../../api/hooks/reports/useGenerateGoogleSlidesReport';
import { logEvent } from '../../../analytics';
import { Page } from '../../../utils/enums';
import useTagTypes from '../../../api/hooks/tags/useTagTypes';
import usePromotedObjectFilter from '../../../components/FilterBar/hooks/usePromotedObjectFilter';
import useCustomTagsFilter from '../../../components/FilterBar/hooks/useCustomTagsFilter';
import useNetworkFilter from '../../../components/FilterBar/hooks/useNetworkFilter';
import useTrendSlidesToIncludeSelector from './hooks/useTrendSlidesToIncludeSelector';
import IntroTagsToggle, { INTRO_TAGS_MAX_FIRST_APPEARANCE_SECS } from '../../../components/IntroTagsToggle';
import BreakdownFilter from '../../../components/FilterBar/Filters/BreakdownFilter';
import useBreakdownFilter from '../../../components/FilterBar/hooks/useBreakdownFilter';
import useAgencyFilter from '../../../components/FilterBar/hooks/useAgencyFilter';
import AgencyFilter, { AgencyFilterProps } from '../../../components/FilterBar/Filters/AgencyFilter';
import {
  useCampaignTypeFilterOptions,
  type CampaignTypeSelection,
} from '../../../components/FilterBar/hooks/useCampaignTypeFilter';
import { FilterActions } from '../../../store/filters';
import useMarketAppsFilter from '../../../components/FilterBar/hooks/useMarketAppsFilter';
import useMarketTagsFilter from '../../../components/FilterBar/hooks/useMarketTagsFilter';

const DISALLOWED_METRICS_TO_DISPLAY: SDK.MetricKPIWithSpend[] = [SDK.Metrics.SPEND];

const TopLevelContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: 100%;
  margin-bottom: 15rem;
`;

const MainContainer = styled.div`
  display: flex;
  gap: 4rem;
  width: 100%;
`;

const FormContainer = styled.div`
  display: flex;
  flex: 2;
  flex-wrap: wrap;
  gap: 1rem;
  width: 100%;
`;

const TextContainer = styled.div`
  width: 30%;
`;

const FiltersContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-top: 1rem;
`;
const CustomizationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 1.1rem;
`;

const CustomizationChipsContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  margin-top: 1rem;
`;

const FilterChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  width: 38rem;
  row-gap: 1rem;
`;

const FilterLineContainer = styled.div<{ columnGap?: number }>`
  display: flex;
  justify-content: flex-end;
  width: 38rem;
  ${(props) => (props.columnGap ? `column-gap: ${props.columnGap}rem;` : '')}
`;

const ImageContainer = styled.div`
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  gap: 1rem;
  min-width: 24rem;
`;

const StyledImage = styled.img`
  width: 100%;
  border-style: solid;
  opacity: 0.5;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  margin-top: 1.25rem;
  margin-bottom: 1.5625rem;
  background-color: ${Colors.Gray[200]};
`;

const InvalidTextTypography = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 0.25rem;
`;

const NUMBER_OF_CUSTOMIZABLE_ROWS_TOP_CREATIVES = 8;
const NUMBER_OF_CUSTOMIZABLE_ROWS_TRENDS = 6;

// Custom comparator function to sort tags by kind and then by type
function compareTags(tagA: { type: string; kind: string }, tagB: { type: string; kind: string }) {
  if (tagA.kind > tagB.kind) {
    return -1;
  }
  if (tagA.kind < tagB.kind) {
    return 1;
  }

  if (tagA.type < tagB.type) {
    return -1;
  }
  if (tagA.type > tagB.type) {
    return 1;
  }

  return 0;
}

const ReportsGoogleSlides = () => {
  const [isTechMerc1456Enabled] = useFeature('techmerc-1456'); // Trends template.
  const [isTechMerc1525Enabled] = useFeature('techmerc-1525'); // Buying Strategy filter
  const [isTechMerc1513Enabled] = useFeature('techmerc-1513'); // Winners DNA Template
  const [isTechMerc1888Enabled] = useFeature('techmerc-1888'); // Market Template
  const [isTechMerc1550Enabled] = useFeature('techmerc-1550'); // Agency filter

  const {
    template,
    selectedKpis,
    selectedTagTypes,
    selectedDate,
    selectedCompareToDate,
    ageSelectedDate,
    countries,
    maxFirstAppearanceSeconds,
    networks,
    adSetIdsToConsider,
    adSetIdsToExclude,
    campaignIdsToConsider,
    campaignIdsToExclude,
    campaignNamePatternsToConsider,
    campaignNamePatternsToExclude,
    campaignNamePatternsInitialized,
    promotedObjectTypes,
    tagsToConsider,
    tagsToExclude,
    excludedTags,
    trendSlidesToInclude,
    breakdown,
    minSpend: defaultMinSpend,
    maxTopItems,
    conceptLevel,
    templateId,
    defaultSortByKpi,
  } = useSelector((state: RootState) => state.reports);

  const {
    baseMetric,
    id: projectId,
    networks: projectNetworks,
    organizationName,
    config: { allowUntaggedMarketAssets, defaultProjectKpis, marketGenres: projectMarketGenres, tagsTypes },
  } = useSelector((state: RootState) => state.project);

  const filters = useSelector((state: RootState) => state.filters);
  const userEmail = useSelector((state: RootState) => state.auth.email);

  const shouldFilterByNetwork = () => networks.length !== 0 && !isEmpty(xor(networks, projectNetworks));

  const [formError, setFormError] = useState<string | null>(null);
  const [sortByKpi, setSortByKpi] = useState<string>(defaultSortByKpi ?? baseMetric);
  const [minSpend, setMinSpend] = useState<number | null>(defaultMinSpend ?? null);

  const generateReportMutation = useGenerateGoogleSlidesReport();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (
      template === SDK.GoogleSlidesTemplates.TOP_CREATIVES &&
      (selectedKpis?.length ?? 0) + (selectedTagTypes?.length ?? 0) > NUMBER_OF_CUSTOMIZABLE_ROWS_TOP_CREATIVES
    ) {
      setFormError(`You cannot select more than ${NUMBER_OF_CUSTOMIZABLE_ROWS_TOP_CREATIVES} values on this section`);
      return;
    }
    if (
      template === SDK.GoogleSlidesTemplates.TRENDS &&
      (selectedKpis?.length ?? 0) + (selectedTagTypes?.length ?? 0) > NUMBER_OF_CUSTOMIZABLE_ROWS_TRENDS
    ) {
      setFormError(`You cannot select more than ${NUMBER_OF_CUSTOMIZABLE_ROWS_TRENDS} values on this section`);
      return;
    }
    if (template === SDK.GoogleSlidesTemplates.TRENDS && trendSlidesToInclude.length <= 0) {
      setFormError('Select at least one type of slide to include.');
      return;
    }
    setFormError(null);
  }, [selectedKpis, selectedTagTypes, template, trendSlidesToInclude]);

  // Build network filter
  const { networkFilterOptions, isLoading: isNetworkFilterLoading } = useNetworkFilter(Page.Reports, networks);

  const networkFilter = useMemo(
    () =>
      ({
        options: networkFilterOptions,
        onChange: (options: MultiSelectDropDownOption[]) => {
          const selectedOptions = options
            .slice(1)
            .filter(({ selected }) => selected)
            .map(({ label }) => (label ?? '').toLowerCase() as SDK.Network);
          dispatch(ReportsActions.changeNetworks({ value: selectedOptions }));
        },
        loading: isNetworkFilterLoading,
      } as NetworkFilterProps),
    [networkFilterOptions, isNetworkFilterLoading, dispatch]
  );

  // Build country filter
  const {
    countryFilterOptions,
    getUpdatedCountries,
    isLoading: countryFilterIsLoading,
    setIsInitialFilter: setIsInitialFilterCountry,
  } = useCountryFilter({
    selectedCountries: countries,
  });
  const countryFilter = useMemo(
    () =>
      ({
        options: countryFilterOptions,
        onChange: (options) => {
          dispatch(
            ReportsActions.changeCountries({
              value: getUpdatedCountries(options),
            })
          );

          setIsInitialFilterCountry(false);
        },
        loading: countryFilterIsLoading,
      } as CountryFilterProps),
    [dispatch, countryFilterIsLoading, countryFilterOptions, getUpdatedCountries, setIsInitialFilterCountry]
  );

  // Build Campaign Filter
  const { campaignFilterOptions, isLoading: campaignFilterIsLoading } = useCampaignFilter({
    initialOptions: campaignIdsToConsider,
    excludedOptions: campaignIdsToExclude,
  });
  const campaignFilter: CampaignFilterProps = useMemo(
    () => ({
      options: campaignFilterOptions,
      loading: campaignFilterIsLoading,
      onChange: (options) => {
        const isAllSelected =
          options.some(({ isAllOption, selected }) => isAllOption && selected) &&
          options.every(({ selected }) => !!selected);

        const selectedOptions = options
          .filter(({ selected, isAllOption }) => selected && !isAllOption)
          .map(({ id }) => id)
          .filter((v): v is string => typeof v === 'string');

        const unselectedOptions = options
          .filter(({ selected, isAllOption }) => !selected && !isAllOption)
          .map(({ id }) => id)
          .filter((v): v is string => typeof v === 'string');

        const toExclude = selectedOptions.length > unselectedOptions.length;

        dispatch(
          ReportsActions.changeCampaigns({
            value: isAllSelected ? [] : toExclude ? unselectedOptions : selectedOptions,
            toExclude,
          })
        );
      },
    }),
    [campaignFilterOptions, campaignFilterIsLoading, dispatch]
  );

  // Build Campaign Type Filter
  const campaignTypeSelection = useMemo<CampaignTypeSelection>(
    () => ({ toConsider: campaignNamePatternsToConsider, toExclude: campaignNamePatternsToExclude }),
    [campaignNamePatternsToConsider, campaignNamePatternsToExclude]
  );
  const onCampaignTypeSelectionChange = useCallback(
    (selection: CampaignTypeSelection) => dispatch(ReportsActions.changeCampaignNamePatterns(selection)),
    [dispatch]
  );
  const {
    options: campaignTypeFilterOptions,
    isLoading: campaignTypeFilterIsLoading,
    hasData: campaignTypeFilterHasData,
    onOptionsChange: campaignTypeFilterOnChange,
  } = useCampaignTypeFilterOptions({
    selection: campaignTypeSelection,
    initialized: campaignNamePatternsInitialized,
    onChange: onCampaignTypeSelectionChange,
  });
  const campaignTypeFilter: CampaignTypeFilterProps = useMemo(
    () => ({
      options: campaignTypeFilterOptions,
      loading: campaignTypeFilterIsLoading,
      hasData: campaignTypeFilterHasData,
      onChange: campaignTypeFilterOnChange,
    }),
    [campaignTypeFilterOptions, campaignTypeFilterIsLoading, campaignTypeFilterHasData, campaignTypeFilterOnChange]
  );

  // Build Buying Strategy Filter
  const {
    buyingStrategyFilterOptions,
    buyingStrategyFilterOnChange,
    isLoading: isBuyingStrategyFilterLoading,
  } = useBuyingStrategyFilter(Page.Reports);
  const buyingStrategyFilter = useMemo(
    () =>
      ({
        options: buyingStrategyFilterOptions,
        onChange: buyingStrategyFilterOnChange,
        loading: isBuyingStrategyFilterLoading,
      } as BuyingStrategyFilterProps),
    [buyingStrategyFilterOptions, buyingStrategyFilterOnChange, isBuyingStrategyFilterLoading]
  );

  // Build agency filter
  const { agencyFilterOptions, agencyFilterOnChange, isLoading: isAgencyFilterLoading } = useAgencyFilter(Page.Reports);
  const agencyFilter = useMemo(
    () =>
      ({
        options: agencyFilterOptions,
        onChange: agencyFilterOnChange,
        loading: isAgencyFilterLoading,
      } as AgencyFilterProps),
    [agencyFilterOptions, agencyFilterOnChange, isAgencyFilterLoading]
  );

  // Build Breakdown filter
  const onBreakdownChange = (value: SDK.Breakdown) => {
    dispatch(
      ReportsActions.changeBreakdown({
        value,
      })
    );
  };

  const { breakdownFilterOptions } = useBreakdownFilter({ breakdown, onChange: onBreakdownChange });

  const { adSetFilterOptions, isLoading: adSetFilterIsLoading } = useAdSetFilter({
    initialOptions: adSetIdsToConsider,
    excludedOptions: adSetIdsToExclude,
  });
  const adSetFilter: AdSetFilterProps = useMemo(
    () => ({
      options: adSetFilterOptions,
      loading: adSetFilterIsLoading,
      onChange: (options) => {
        const isAllSelected =
          options.some(({ isAllOption, selected }) => isAllOption && selected) &&
          options.every(({ selected }) => !!selected);

        const selectedOptions = options
          .filter(({ selected, isAllOption }) => selected && !isAllOption)
          .map(({ id }) => id)
          .filter((v): v is string => typeof v === 'string');

        const unselectedOptions = options
          .filter(({ selected, isAllOption }) => !selected && !isAllOption)
          .map(({ id }) => id)
          .filter((v): v is string => typeof v === 'string');

        const toExclude = selectedOptions.length > unselectedOptions.length;

        dispatch(
          ReportsActions.changeAdSets({
            value: isAllSelected ? [] : toExclude ? unselectedOptions : selectedOptions,
            toExclude,
          })
        );
      },
    }),
    [adSetFilterOptions, dispatch, adSetFilterIsLoading]
  );

  const {
    marketAppsFilterOptions,
    marketAppsFilterOnChange,
    loading: appFilterLoading,
    onClearClick,
  } = useMarketAppsFilter(Page.Reports);

  const marketAppsFilter = useMemo(
    () =>
      ({
        options: marketAppsFilterOptions,
        onChange: marketAppsFilterOnChange,
        loading: appFilterLoading,
        onClearClick,
      } as MarketAppsFilterProps),
    [marketAppsFilterOptions, marketAppsFilterOnChange, appFilterLoading, onClearClick]
  );

  const { marketTagsFilterOptions, marketTagsFilterOnChange, loading: marketTagsFilterLoading } = useMarketTagsFilter();
  const marketTagsFilter = useMemo(
    () =>
      ({
        options: marketTagsFilterOptions,
        onChange: marketTagsFilterOnChange,
        loading: marketTagsFilterLoading,
      } as CustomMarketTagsFilterProps),
    [marketTagsFilterOptions, marketTagsFilterOnChange, marketTagsFilterLoading]
  );

  // KPI Selector
  const kpiFilterOptions = Array.from(new Set([...defaultProjectKpis, SDK.Metrics.IMPRESSIONS]))
    .filter((kpi) => kpi !== SDK.Metrics.AGE)
    .map((kpi) => ({
      label: SDK.kpiUtils.getDisplayName(kpi),
      onClick: () => setSortByKpi(kpi),
    }));
  const kpiFilter = useMemo<KpiFilterProps>(
    () => ({
      dropdownType: 'singleselect',
      options: kpiFilterOptions,
      defaultOption: sortByKpi,
    }),
    [kpiFilterOptions, sortByKpi]
  );

  const { data: tags, isLoading: isTagsLoading } = useTagTypes({
    projectIds: [projectId],
    tagsFilters: {
      tagTypesToExclude: [...(tagsTypes?.excluded ?? []), ...(tagsTypes?.psychological ?? [])].map((type) => ({
        type,
      })),
    },
  });

  const tagTypesSelectorOptions = useMemo(
    () =>
      [...new Set(tags?.sort(compareTags)?.map((tag) => tag.type))].map((tagType) => ({
        id: tagType,
        label: camelCaseToCapitalCase(tagType),
        selected: selectedTagTypes.includes(tagType),
      })) ?? [],
    [tags]
  );

  const tagTypesSelectorOnChange = useCallback(
    (options: MultiSelectDropDownOption[]) => {
      dispatch(
        ReportsActions.setSelectedTagTypes(
          options?.filter((option) => option.selected).map((option) => option.id ?? '') ?? []
        )
      );
    },
    [dispatch]
  );

  const kpisSelectorOptions = useMemo(
    () =>
      Array.from(
        new Set([
          ...defaultProjectKpis,
          SDK.Metrics.INSTALLS,
          SDK.Metrics.AGE,
          SDK.Metrics.CLICKS,
          SDK.Metrics.IMPRESSIONS,
        ])
      )
        .filter((kpi) => !DISALLOWED_METRICS_TO_DISPLAY.includes(kpi))
        .map((kpi) => ({
          id: kpi,
          label: SDK.kpiUtils.getDisplayName(kpi),
          selected: selectedKpis.includes(kpi),
        })),
    [defaultProjectKpis]
  );

  const kpisSelectorOnChange = useCallback(
    (options: MultiSelectDropDownOption[]) => {
      dispatch(
        ReportsActions.setSelectedKpis(
          options?.filter((option) => option.selected).map((option) => (option.id ?? '') as SDK.MetricOrKpi) ?? []
        )
      );
    },
    [dispatch]
  );

  // Build promoted object types filter
  const { promotedObjectTypeFilterOptions, setIsInitialFilter: setIsInitialFilterPOT } = usePromotedObjectFilter(
    Page.Reports,
    promotedObjectTypes
  );
  const promotedObjectFilter: PromotedObjectFilterProps = useMemo(
    () => ({
      options: promotedObjectTypeFilterOptions,
      onChange: (options) => {
        setIsInitialFilterPOT(false);

        const selectedOptions = options
          .slice(1)
          .filter(({ selected }) => selected)
          .map(({ id }) => id as SDK.PromotedObjectType);

        dispatch(
          ReportsActions.changePromotedObjectTypes({
            value: selectedOptions,
          })
        );
      },
      loading: false,
    }),
    [dispatch, promotedObjectTypeFilterOptions, setIsInitialFilterPOT]
  );

  // Tags filter
  const {
    customTagsFilterOptions,
    customTagsFilterOnChange,
    isLoading: isCustomTagsFilterLoading,
  } = useCustomTagsFilter('reports');
  const customTagsFilter: CustomTagsFilterProps = useMemo(
    () => ({
      options: customTagsFilterOptions,
      onChange: customTagsFilterOnChange,
      loading: isCustomTagsFilterLoading,
    }),
    [customTagsFilterOptions, isCustomTagsFilterLoading, customTagsFilterOnChange]
  );

  // Exclude Tags filter
  const {
    customTagsFilterOptions: excludedTagsFilterOptions,
    customTagsFilterOnChange: excludedTagsFilterOnChange,
    isLoading: isExcludedTagsFilterLoading,
  } = useCustomTagsFilter('reportsExcluded');
  const excludedTagsFilter: CustomTagsFilterProps = useMemo(
    () => ({
      options: excludedTagsFilterOptions,
      onChange: excludedTagsFilterOnChange,
      loading: isExcludedTagsFilterLoading,
    }),
    [excludedTagsFilterOptions, isExcludedTagsFilterLoading, excludedTagsFilterOnChange]
  );

  // Trend slides to include selector
  const { trendSlidesToIncludeSelectorOptions, trendSlidesToIncludeSelectorOnChange } =
    useTrendSlidesToIncludeSelector();

  const onGenerateReport = async () => {
    const marketTagsToConsider = marketTagsFilterOptions
      .filter((marketTag) => marketTag.selected)
      .map((marketTag) => ({ type: marketTag.type, value: marketTag.value }));

    const body: SDK.ExportToGoogleSlidesRequestBody = {
      templateId,
      reportType: SDK.ReportTypes.GOOGLE_SLIDES,
      appsFilters: {
        appIdsToInclude: filters.marketAppsToConsider.map((ma) => ma.id),
        appIdsToExclude: filters.marketAppsToExclude.map((ma) => ma.id),
        publishersToExclude: [organizationName],
        ...(projectMarketGenres && filters.onlyMarketCompetitors ? { genres: projectMarketGenres } : {}),
      },
      adsFilters: {
        adSetIdsToConsider,
        adSetIdsToExclude,
        campaignIdsToConsider,
        campaignIdsToExclude,
        campaignNamePatternsToConsider,
        campaignNamePatternsToExclude,
        promotedObjectTypesToConsider: promotedObjectTypes,
        networksToConsider: shouldFilterByNetwork() ? networks : undefined,
      },
      assetFilters: {
        minSpend: minSpend ?? undefined,
      },
      assetTagsFilters: {
        maxFirstAppearanceSeconds:
          template === SDK.GoogleSlidesTemplates.TRENDS ? maxFirstAppearanceSeconds ?? undefined : undefined,
      },
      compareToTimeRange: {
        dateStartTimestamp: new Date(`${selectedCompareToDate.startDate ?? ''}T00:00:00.000Z`).getTime(),
        dateEndTimestamp: new Date(`${selectedCompareToDate.endDate ?? ''}T00:00:00.000Z`).getTime(),
      },
      hasPostQaAnnotations: allowUntaggedMarketAssets ? false : !!filters.onlyMarketCompetitors,
      mainKpi: sortByKpi as SDK.MetricOrKpi,
      metricsFilters: api.filterConverter.getMetricsFilters({
        countries,
        startDate: selectedDate.startDate,
        endDate: selectedDate.endDate,
        ageStartDate: ageSelectedDate.ageStartDate,
        ageEndDate: ageSelectedDate.ageEndDate,
      }),
      tagsFilters: api.filterConverter.getTagsFilters({
        tagsToConsider: template !== SDK.GoogleSlidesTemplates.MARKET ? tagsToConsider : marketTagsToConsider,
        tagsToExclude: template !== SDK.GoogleSlidesTemplates.MARKET ? tagsToExclude : [],
        // The market template filters on market tags, a different tag set
        excludedTags: template !== SDK.GoogleSlidesTemplates.MARKET ? excludedTags : [],
      }),
      projectIds: [projectId],
      shareWithEmail: userEmail,
      metricsToDisplay: selectedKpis,
      tagTypesToDisplay: selectedTagTypes,
      conceptLevel,
      maxTopItems,
      template,
      trendSlidesToInclude,
      breakdown,
    };

    await generateReportMutation.mutateAsync(body, {
      onSuccess: () => {
        dispatch(ReportsActions.setGeneratingReportNotification(GeneratingReportNotificationStatus.SUCCESS));
        navigate(`/${projectId}/${Page.Reports}`);
      },
      onError: () => {
        dispatch(ReportsActions.setGeneratingReportNotification(GeneratingReportNotificationStatus.FAILURE));
      },
      onSettled: () => {
        logEvent({
          component: 'Reports',
          action: `Generate report`,
          category: 'user_actions',
        });
      },
    });
  };

  const tabLabels = useMemo(
    () => [
      { id: SDK.GoogleSlidesTemplates.TOP_CREATIVES, label: 'Metrics Report' },
      {
        disabled: !isTechMerc1456Enabled,
        id: SDK.GoogleSlidesTemplates.TRENDS,
        label: 'Trends Report',
        tooltipContent: isTechMerc1456Enabled ? undefined : <Typography>Coming soon.</Typography>,
      },
      ...(isTechMerc1513Enabled ? [{ id: SDK.GoogleSlidesTemplates.WINNERS_DNA, label: 'Winners DNA Report' }] : []),
      ...(isTechMerc1888Enabled ? [{ id: SDK.GoogleSlidesTemplates.MARKET, label: 'Market Report' }] : []),
    ],
    [isTechMerc1456Enabled, isTechMerc1513Enabled, isTechMerc1888Enabled]
  );

  return (
    <TopLevelContainer>
      <TopNavPageTitle
        title="Google Slides Report"
        subtitle="Generate a Google Slides presentation from our templates"
        eventPrefix="reports-google-slides"
        navigateToPage={Page.Reports}
      />

      <Tabs
        onTabChange={(newTab) => {
          logEvent({
            action: `Go to tab ${newTab.id ?? SDK.messages.NOT_AVAILABLE}`,
            component: 'Google Slides Report',
          });
          dispatch(ReportsActions.setTemplate(newTab.id as SDK.GoogleSlidesTemplates));
        }}
        tabLabels={tabLabels}
        defaultSelectedTab={tabLabels.find((tab) => tab.id === template)}
      >
        <MainContainer>
          <FormContainer>
            <div>
              <FiltersContainer>
                <TextContainer>
                  <Typography fontWeight="semi-bold" type="text-lg">
                    Filters
                  </Typography>
                  <Typography type="text-sm">
                    {template === SDK.GoogleSlidesTemplates.TRENDS
                      ? 'Narrow the data you want the report to be about. The trends report will be computed by comparing metrics between the two time ranges selected.'
                      : 'Narrow the data you want the report to be about.'}
                  </Typography>
                </TextContainer>
                <FilterChipsContainer>
                  <FilterLineContainer columnGap={0.75}>
                    {![SDK.GoogleSlidesTemplates.WINNERS_DNA, SDK.GoogleSlidesTemplates.MARKET].includes(template) && (
                      <>
                        <CampaignFilter
                          options={campaignFilter.options}
                          onChange={campaignFilter.onChange}
                          loading={campaignFilter.loading}
                          dropdownMaxHeightInVH={65}
                        />
                        {(campaignTypeFilter.loading || campaignTypeFilter.hasData) && (
                          <CampaignTypeFilter
                            options={campaignTypeFilter.options}
                            onChange={campaignTypeFilter.onChange}
                            loading={campaignTypeFilter.loading}
                            dropdownMaxHeightInVH={65}
                          />
                        )}
                        {isTechMerc1525Enabled && (
                          <BuyingStrategyFilter
                            options={buyingStrategyFilter.options}
                            onChange={buyingStrategyFilter.onChange}
                            loading={buyingStrategyFilter.loading}
                          />
                        )}
                        {isTechMerc1550Enabled && (
                          <AgencyFilter
                            options={agencyFilter.options}
                            onChange={agencyFilter.onChange}
                            loading={agencyFilter.loading}
                          />
                        )}
                      </>
                    )}
                  </FilterLineContainer>
                  {![SDK.GoogleSlidesTemplates.MARKET].includes(template) && (
                    <FilterLineContainer columnGap={0.75}>
                      <CountryFilter
                        options={countryFilter.options}
                        onChange={countryFilter.onChange}
                        loading={countryFilter.loading}
                        dropdownMaxHeightInVH={65}
                      />
                      <NetworkFilter
                        options={networkFilter.options}
                        onChange={networkFilter.onChange}
                        disabled={networkFilter.disabled}
                        loading={networkFilter.loading}
                      />
                    </FilterLineContainer>
                  )}
                  {![SDK.GoogleSlidesTemplates.WINNERS_DNA, SDK.GoogleSlidesTemplates.MARKET].includes(template) && (
                    <FilterLineContainer columnGap={0.75}>
                      <AdSetFilter
                        options={adSetFilter.options}
                        onChange={adSetFilter.onChange}
                        loading={adSetFilter.loading}
                        dropdownMaxHeightInVH={65}
                      />
                    </FilterLineContainer>
                  )}
                  {![SDK.GoogleSlidesTemplates.MARKET].includes(template) && (
                    <FilterLineContainer columnGap={0.75}>
                      <BreakdownFilter options={breakdownFilterOptions} />
                    </FilterLineContainer>
                  )}
                  {template === SDK.GoogleSlidesTemplates.TRENDS ? (
                    <>
                      <FilterLineContainer columnGap={0.75}>
                        <Typography noMargin style={{ marginTop: '0.75rem', textAlign: 'end' }} type="text-xs">
                          1st Period:
                        </Typography>
                        <DateFilter
                          customDispatch={ReportsActions.changeCompareToTimeRange}
                          customDates={selectedCompareToDate}
                        />
                      </FilterLineContainer>
                      <FilterLineContainer columnGap={0.75}>
                        <Typography noMargin style={{ marginTop: '0.75rem', textAlign: 'end' }} type="text-xs">
                          2nd Period:
                        </Typography>
                        <DateFilter customDispatch={ReportsActions.changeDateRange} customDates={selectedDate} />
                      </FilterLineContainer>
                      <FilterLineContainer columnGap={0.75}>
                        <AgeDateFilter
                          customDispatch={ReportsActions.changeAgeDateRange}
                          customDates={ageSelectedDate}
                        />
                      </FilterLineContainer>
                    </>
                  ) : (
                    <FilterLineContainer columnGap={0.75}>
                      <DateFilter customDispatch={ReportsActions.changeDateRange} customDates={selectedDate} />
                      <AgeDateFilter customDispatch={ReportsActions.changeAgeDateRange} customDates={ageSelectedDate} />
                    </FilterLineContainer>
                  )}
                  {![SDK.GoogleSlidesTemplates.MARKET].includes(template) && (
                    <FilterLineContainer columnGap={0.75}>
                      <SpendFilter
                        customState={{
                          value: minSpend,
                          onChange: setMinSpend,
                        }}
                      />
                      <PromotedObjectFilter
                        options={promotedObjectFilter.options}
                        onChange={promotedObjectFilter.onChange}
                        disabled={promotedObjectFilter.disabled}
                        loading={promotedObjectFilter.loading}
                      />
                    </FilterLineContainer>
                  )}
                  {![SDK.GoogleSlidesTemplates.MARKET].includes(template) && (
                    <FilterLineContainer columnGap={0.75}>
                      <CustomTagsFilter
                        options={customTagsFilter.options}
                        onChange={customTagsFilter.onChange}
                        loading={customTagsFilter.loading}
                      />
                      <CustomTagsFilter
                        options={excludedTagsFilter.options}
                        onChange={excludedTagsFilter.onChange}
                        loading={excludedTagsFilter.loading}
                        variant="exclude"
                      />
                    </FilterLineContainer>
                  )}
                  {[SDK.GoogleSlidesTemplates.MARKET].includes(template) && (
                    <Toggle
                      label="Only my competitors"
                      variant="outlined"
                      checked={filters.onlyMarketCompetitors}
                      onChange={(value: boolean) => {
                        // Unselect tags if we want all verticals
                        if (!value) dispatch(FilterActions.changeMarketTags({ value: [], logEvent: false }));
                        dispatch(FilterActions.changeOnlyMarketCompetitors({ value }));
                      }}
                    />
                  )}
                  {[SDK.GoogleSlidesTemplates.MARKET].includes(template) && (
                    <MarketAppsFilter
                      options={marketAppsFilter.options}
                      onChange={marketAppsFilter.onChange}
                      loading={marketAppsFilter.loading}
                      onClearClick={marketAppsFilter.onClearClick}
                    />
                  )}
                  {[SDK.GoogleSlidesTemplates.MARKET].includes(template) && (
                    <CustomMarketTagsFilter
                      options={marketTagsFilter.options}
                      onChange={marketTagsFilter.onChange}
                      loading={marketTagsFilter.loading}
                    />
                  )}
                  <FilterLineContainer columnGap={0.75}>
                    {template === SDK.GoogleSlidesTemplates.TRENDS && (
                      <IntroTagsToggle
                        initialValue={maxFirstAppearanceSeconds ?? undefined}
                        customDispatch={(checked) =>
                          ReportsActions.changeMaxFirstAppearanceSeconds({
                            value: checked ? INTRO_TAGS_MAX_FIRST_APPEARANCE_SECS : undefined,
                          })
                        }
                      />
                    )}
                    {![SDK.GoogleSlidesTemplates.MARKET].includes(template) && (
                      <Toggle
                        variant="outlined"
                        label="Concept Level"
                        labelPosition="start"
                        checked={conceptLevel}
                        onChange={() => dispatch(ReportsActions.changeConceptLevel(!conceptLevel))}
                      />
                    )}
                    <DropDownButton
                      onClickCrossButton={() => dispatch(ReportsActions.changeMaxTopItems(null))}
                      onConfirm={(value) =>
                        dispatch(ReportsActions.changeMaxTopItems(Number.parseInt(value.toString(), 10)))
                      }
                      placeHolder={`Top ${maxTopItems} items`}
                      defaultValue={maxTopItems}
                      title="Max top items"
                    />
                  </FilterLineContainer>
                </FilterChipsContainer>
              </FiltersContainer>
              {![SDK.GoogleSlidesTemplates.WINNERS_DNA, SDK.GoogleSlidesTemplates.MARKET].includes(template) && (
                <>
                  <Divider />
                  <CustomizationContainer>
                    <TextContainer>
                      <Typography fontWeight="semi-bold" type="text-lg">
                        Customization
                      </Typography>
                      <Typography type="text-sm">
                        Select the information you want to see in the report. The total number of custom rows to display
                        cannot be higher than{' '}
                        {template === SDK.GoogleSlidesTemplates.TOP_CREATIVES
                          ? NUMBER_OF_CUSTOMIZABLE_ROWS_TOP_CREATIVES
                          : NUMBER_OF_CUSTOMIZABLE_ROWS_TRENDS}
                        .
                      </Typography>
                    </TextContainer>
                    <CustomizationChipsContainer>
                      <KpiFilter
                        options={kpiFilter.options}
                        defaultOption={SDK.kpiUtils.getDisplayName(kpiFilter.defaultOption)}
                        dropdownType={kpiFilter.dropdownType}
                        onChange={kpiFilter.onChange}
                        prefixLabel="Sort by KPI: "
                      />
                      <DropDownChip
                        placeHolder="Select KPIs"
                        dropDownType="multiselect"
                        dropDownOptions={kpisSelectorOptions}
                        onChange={kpisSelectorOnChange}
                        dropdownMaxHeightInVH={30}
                        prefixLabel="KPIs"
                        invalid={!!formError}
                      />
                      {[SDK.GoogleSlidesTemplates.TOP_CREATIVES].includes(template) ? (
                        <DropDownChip
                          placeHolder="Select Tag Types"
                          dropDownType="multiselect"
                          dropDownOptions={tagTypesSelectorOptions}
                          onChange={tagTypesSelectorOnChange}
                          prefixLabel="Tags"
                          invalid={!!formError}
                          loading={isTagsLoading}
                          dropdownMaxHeightInVH={50}
                        />
                      ) : null}
                      {[SDK.GoogleSlidesTemplates.TRENDS].includes(template) ? (
                        <DropDownChip
                          placeHolder="Select slides to include"
                          dropDownType="multiselect"
                          dropDownOptions={trendSlidesToIncludeSelectorOptions}
                          onChange={trendSlidesToIncludeSelectorOnChange}
                          prefixLabel="Slides to include"
                          invalid={!!formError}
                          loading={isTagsLoading}
                          dropdownMaxHeightInVH={50}
                        />
                      ) : null}
                    </CustomizationChipsContainer>
                  </CustomizationContainer>
                </>
              )}
              {formError ? (
                <InvalidTextTypography>
                  <TypographyClean type="text-sm" color={Colors.Error[400]}>
                    {formError}
                  </TypographyClean>
                </InvalidTextTypography>
              ) : (
                ''
              )}
              <Divider />

              <ButtonContainer>
                <Button
                  leadingIcon={generateReportMutation?.isLoading ? { name: 'LoadingCircle' } : { name: 'Sparkles' }}
                  onClick={() => {
                    onGenerateReport();
                  }}
                  disabled={generateReportMutation?.isLoading || !!formError}
                >
                  Generate Report
                </Button>
              </ButtonContainer>
            </div>
          </FormContainer>
          <ImageContainer>
            <TypographyClean fontWeight="semi-bold" type="text-lg">
              Current template:
            </TypographyClean>
            <StyledImage
              key={`image-${template}-1`}
              src={SDK.getReportTemplateImageUrl({
                reportType: SDK.ReportTypes.GOOGLE_SLIDES,
                reportTemplate: template,
                index: 1,
              })}
            />
            {![SDK.GoogleSlidesTemplates.WINNERS_DNA, SDK.GoogleSlidesTemplates.MARKET].includes(template) && (
              <StyledImage
                key={`image-${template}-2`}
                src={SDK.getReportTemplateImageUrl({
                  reportType: SDK.ReportTypes.GOOGLE_SLIDES,
                  reportTemplate: template,
                  index: 2,
                })}
              />
            )}
          </ImageContainer>
        </MainContainer>
      </Tabs>
    </TopLevelContainer>
  );
};

export default ReportsGoogleSlides;
